import { AssetSubClassCode } from '@api/api/entity-constants';
import { type Entity } from '@api/api/entity-schema';
import { CheckBox } from '@design/components/checkbox/CheckBox';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { Formats, formatDateToLocale } from '@helpers/utils/dates/dates';
import { capitalizeFirstLetter } from '@helpers/utils/strings/strings';
import {
  type ColumnConfig,
  ColumnTable,
  ColumnTableCell,
  type ColumnTableCellProps,
  type ColumnTableRow,
} from '@terminal/components/column-table';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { useDocumentsEntity } from '@terminal/react-query-hooks/documents/useDocumentsEntity';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import clsx from 'clsx';
import { parseISO } from 'date-fns';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import earningsStyles from '../earnings/Earnings.module.scss';
import styles from './DocumentWidget.module.scss';
import { useDocumentWidgetStore } from './useDocumentWidgetStore';
import { DocumentTableItem, groupDocumentByYear, isFirstItem } from './utils';

export interface DocumentProps {
  entityTag: string;
  entitySubClass?: Entity['sub_class'];
}

type StringKeys<T> = Extract<keyof T, string>;

type DocumentTableColumn = {
  key: StringKeys<DocumentTableItem>;
  label: string;
  render?: (value: unknown, item: DocumentTableItem) => ReactNode;
  renderHeader?: () => ReactNode;
  renderCell?: (props: ColumnTableCellProps<DocumentTableItem>) => ReactNode;
};

export const Document = ({ entityTag, entitySubClass }: DocumentProps) => {
  const isCentralBank = entitySubClass === AssetSubClassCode.EcoCentralBank;
  const { t } = useTranslation(['widget', 'asset-overview', 'chat', 'common']);
  const languageSetting = useLanguageStore(state => state.languageSetting);
  const isNewEarningsTabEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-4671-ao-earnings')
  );

  const {
    data: documents,
    isError,
    isFetching,
    error,
  } = useDocumentsEntity(entityTag);

  const {
    initStore,
    getIsRowSelected,
    updateRowSelection,
    toggleAllRowsSelection,
    getIsAllRowsSelected,
    getIsSomeRowsSelected,
  } = useDocumentWidgetStore(state => ({
    initStore: state.initStore,
    rowSelection: state.rowSelection,
    getIsRowSelected: state.getIsRowSelected,
    updateRowSelection: state.updateRowSelection,
    toggleAllRowsSelection: state.toggleAllRowsSelection,
    getIsAllRowsSelected: state.getIsAllRowsSelected,
    getIsSomeRowsSelected: state.getIsSomeRowsSelected,
  }));

  useEffect(() => {
    if (!isFetching) {
      initStore(documents ?? []);
    }
  }, [isFetching, documents, initStore]);

  const docsGroupedByYear = groupDocumentByYear(documents);

  if (isError) {
    throw new Error(error.message);
  }

  if (isFetching) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              maxWidth: 'var(--content-max-width)',
              borderRadius: '8px',
              height: '360px',
              margin: '20px auto',
            },
          },
        ]}
        data-testid="document-widget-loader"
      />
    );
  }

  if (!documents?.length) {
    return (
      <ErrorMessageWrapper
        className={styles.styledErrorWrapper}
        displayIcon
        variant="empty"
      />
    );
  }

  const columns: DocumentTableColumn[] = [
    {
      label: '',
      key: 'id',
      renderHeader: () => (
        <span className={earningsStyles.tableHeaderContent}>
          <CheckBox
            name="all-documents"
            label=""
            aria-label={t('common:selectAll')}
            onChange={(_, e) => toggleAllRowsSelection(e.target.checked)}
            checked={getIsAllRowsSelected() || getIsSomeRowsSelected()}
            partial={getIsSomeRowsSelected() && !getIsAllRowsSelected()}
          />
        </span>
      ),
      render: props => {
        const id = String(props);
        const document = documents?.find(doc => String(doc.id) === id);
        return (
          <div className={earningsStyles.wrapperCheckbox}>
            <CheckBox
              className={styles.styledCheckBox}
              name={`document-${id}`}
              label=""
              aria-label={document?.name ?? id}
              checked={getIsRowSelected(id)}
              onChange={() => updateRowSelection(id, !getIsRowSelected(id))}
            />
          </div>
        );
      },
    },
    {
      label: t('widget:assetOverview.tableDocuments.year'),
      key: 'year',
      renderCell: props => (
        <ColumnTableCell
          key={props.column.key}
          className={clsx(styles.cell, {
            [styles.withBorder]: true,
          })}
          {...props}
        />
      ),
      render: (value, item) =>
        Number(value) !== 0 && isFirstItem(docsGroupedByYear, item) ? (
          <span className={styles.numericalValue}>{String(value)}</span>
        ) : (
          <span />
        ),
    },
    {
      label: t('widget:assetOverview.tableDocuments.calendar'),
      key: 'period',
      render: value => (
        <span className={styles.numericalValue}>{String(value)}</span>
      ),
    },
    {
      label: isCentralBank
        ? t('widget:assetOverview.tableDocuments.publicationDate', {
            defaultValue: 'Publication date',
          })
        : t('widget:assetOverview.tableDocuments.filingDate'),
      key: 'release_date',
      render: (_, document) => {
        const date = isCentralBank
          ? parseISO(document.release_date.slice(0, 10))
          : new Date(document.release_date);
        const formatTime = formatDateToLocale(
          languageSetting,
          date,
          Formats['yyyy-MM-dd']
        );
        return <span className={styles.numericalValue}>{formatTime}</span>;
      },
    },
    {
      label: isCentralBank
        ? t('widget:assetOverview.tableDocuments.publicationType', {
            defaultValue: 'Publication type',
          })
        : t('widget:assetOverview.tableDocuments.DocumentType'),
      key: 'kind',
      render: isCentralBank
        ? (_, document) =>
            capitalizeFirstLetter(document.kind.replaceAll('_', ' '))
        : undefined,
    },
    {
      label: t('widget:assetOverview.tableDocuments.companyFiscal'),
      key: 'reference',
    },
    {
      label: isCentralBank
        ? t('widget:assetOverview.tableDocuments.publicationTitle', {
            defaultValue: 'Publication title',
          })
        : t('widget:assetOverview.tableDocuments.file'),
      key: 'name',
      render: (_, document) => (
        <a
          className={styles.pdfLink}
          href={document.location}
          target="_blank"
          rel="noreferrer"
        >
          <span className={styles.pdfWrapper}>
            <img
              className={styles.pdfIcon}
              src="/app/static/images/icons/pdf.svg"
              alt=""
            />
            {document.name}
          </span>
        </a>
      ),
    },
  ];

  const tableData: ColumnTableRow<DocumentTableItem>[] = documents.map(
    document => document as DocumentTableItem
  );

  return (
    <div
      className={clsx(styles.root, {
        [styles.expandedLayout]: isNewEarningsTabEnabled,
      })}
    >
      <ColumnTable<DocumentTableItem>
        className={styles.styledRevampTable}
        columns={
          (isCentralBank
            ? columns.filter(
                column => !['year', 'period', 'reference'].includes(column.key)
              )
            : columns) as ColumnConfig<DocumentTableItem>[]
        }
        data={tableData}
        alignment="left"
        renderActionColumn={() => <></>}
      />
    </div>
  );
};
